import { z } from 'zod';
import {
  avatarColorSchema,
  currencySchema,
  displayNameSchema,
  emojiSchema,
  idSchema,
  isoDateTimeSchema,
  localeSchema,
  timezoneSchema,
} from './common';
import { meSchema } from './users';

/**
 * Session mechanism (for the web app):
 * - Every sign-in endpoint sets an httpOnly, SameSite=Lax cookie (`__Host-split_session` in
 *   production, `split_session` in local dev). The web app never sees the token; just use
 *   `fetch(url, { credentials: 'same-origin' })`.
 * - Writes (POST/PUT/PATCH/DELETE) must come from the same origin: browsers send `Origin`
 *   automatically; requests with a foreign `Origin` get `403 CSRF_REJECTED`.
 * - JSON bodies must use `Content-Type: application/json`.
 */
export const SESSION_COOKIE_NAME = '__Host-split_session';
export const DEV_SESSION_COOKIE_NAME = 'split_session';

/** Profile fields chosen during onboarding. */
const onboardingProfileSchema = z.object({
  displayName: displayNameSchema,
  avatarEmoji: emojiSchema.nullable().optional(),
  avatarColor: avatarColorSchema.nullable().optional(),
  locale: localeSchema.optional(),
  timezone: timezoneSchema.optional(),
  defaultCurrency: currencySchema.optional(),
  /** Free text shown in the Devices list; defaults to a name derived from the User-Agent. */
  deviceName: z.string().trim().min(1).max(60).optional(),
});

/** `POST /api/setup`: bootstraps the first admin with `ADMIN_SETUP_SECRET`. Works once. */
export const setupRequestSchema = onboardingProfileSchema.extend({
  secret: z.string().min(1).max(500),
});
export type SetupRequest = z.input<typeof setupRequestSchema>;

/** `GET /api/invites/:code` (public). */
export const invitePreviewSchema = z.object({
  status: z.enum(['valid', 'used', 'expired', 'invalid']),
  /** Display name of the admin who created it (only when `valid`). */
  inviterName: z.string().nullable(),
  /** The invite note, used to prefill the display name (only when `valid`). */
  suggestedName: z.string().nullable(),
  expiresAt: isoDateTimeSchema.nullable(),
});
export type InvitePreview = z.infer<typeof invitePreviewSchema>;

/** `POST /api/invites/:code/redeem` (public, rate limited). */
export const redeemInviteRequestSchema = onboardingProfileSchema;
export type RedeemInviteRequest = z.input<typeof redeemInviteRequestSchema>;

/**
 * Response of setup and invite redemption. `recoveryPhrase` (12 BIP39 words) is returned
 * exactly once: the server only stores a hash. Show it in onboarding step 2 and keep it on the
 * device if you want to show it again from Settings.
 */
export const accountCreatedResponseSchema = z.object({
  user: meSchema,
  recoveryPhrase: z.string(),
});
export type AccountCreatedResponse = z.infer<typeof accountCreatedResponseSchema>;

/** `POST /api/auth/recovery` (public, rate limited). */
export const recoverySignInRequestSchema = z.object({
  phrase: z.string().min(1).max(500),
  deviceName: z.string().trim().min(1).max(60).optional(),
});
export type RecoverySignInRequest = z.input<typeof recoverySignInRequestSchema>;

export const sessionUserResponseSchema = z.object({ user: meSchema });
export type SessionUserResponse = z.infer<typeof sessionUserResponseSchema>;

/** `POST /api/me/recovery/rotate`: replaces the phrase; the old one stops working. */
export const recoveryRotateResponseSchema = z.object({ recoveryPhrase: z.string() });
export type RecoveryRotateResponse = z.infer<typeof recoveryRotateResponseSchema>;

/** A signed-in device (`GET /api/me/sessions`). */
export const deviceSessionSchema = z.object({
  id: idSchema,
  deviceName: z.string(),
  createdAt: isoDateTimeSchema,
  lastSeenAt: isoDateTimeSchema,
  current: z.boolean(),
  /** Whether this device has a Web Push subscription. */
  pushEnabled: z.boolean(),
});
export type DeviceSession = z.infer<typeof deviceSessionSchema>;
export const deviceSessionListSchema = z.object({ items: z.array(deviceSessionSchema) });
export type DeviceSessionList = z.infer<typeof deviceSessionListSchema>;

// ---------------------------------------------------------------------------
// Passkeys (WebAuthn). Options/responses are the JSON shapes of @simplewebauthn/browser:
// pass `options` to `startRegistration({ optionsJSON })` / `startAuthentication({ optionsJSON })`
// and send the result back as `response` together with `challengeId`.
// ---------------------------------------------------------------------------

export const passkeySchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: isoDateTimeSchema,
  lastUsedAt: isoDateTimeSchema.nullable(),
  backedUp: z.boolean(),
});
export type Passkey = z.infer<typeof passkeySchema>;
export const passkeyListSchema = z.object({ items: z.array(passkeySchema) });
export type PasskeyList = z.infer<typeof passkeyListSchema>;

export const passkeyOptionsResponseSchema = z.object({
  challengeId: idSchema,
  /** `PublicKeyCredentialCreationOptionsJSON` or `PublicKeyCredentialRequestOptionsJSON`. */
  options: z.record(z.string(), z.unknown()),
});
export type PasskeyOptionsResponse = z.infer<typeof passkeyOptionsResponseSchema>;

export const passkeyRegisterVerifyRequestSchema = z.object({
  challengeId: idSchema,
  /** `RegistrationResponseJSON` from `startRegistration()`. */
  response: z.record(z.string(), z.unknown()),
  name: z.string().trim().min(1).max(60).optional(),
});
export type PasskeyRegisterVerifyRequest = z.input<typeof passkeyRegisterVerifyRequestSchema>;

export const passkeyLoginVerifyRequestSchema = z.object({
  challengeId: idSchema,
  /** `AuthenticationResponseJSON` from `startAuthentication()`. */
  response: z.record(z.string(), z.unknown()),
  deviceName: z.string().trim().min(1).max(60).optional(),
});
export type PasskeyLoginVerifyRequest = z.input<typeof passkeyLoginVerifyRequestSchema>;
